import { levelFromXp, type LevelInfo } from './levels';
import { emptyStats, isPveMode, type GameMode, type MatchEvent, type PlayerOutcome, type PlayerStats } from './types';
import {
  DEFAULT_EQUIPPED,
  getUnlock,
  isUnlocked,
  unlockedIds,
  type UnlockContext,
  type UnlockDef,
  type UnlockKind,
} from './unlocks';
import {
  dailyFirstWinLine,
  isFlawless,
  mergeBreakdowns,
  scoreMatch,
  starsLine,
  type XpBreakdown,
} from './xp';

export interface Equipped {
  body: string;
  outfit: string;
  trail: string;
  /**
   * Creator-role uploads: served paths (`<user>/body.png?v=3`) under /api/ugc/ that replace the
   * drawn torso and head. Only the server sets these (after validating the PNG).
   */
  custom?: { body?: string; head?: string };
}

export interface Profile {
  /** Discord user id. */
  id: string;
  displayName?: string;
  xp: number;
  stats: PlayerStats;
  equipped: Equipped;
  /** Equipped perk ids (max PERK_SLOTS). Ignored in Versus. */
  perks: string[];
  createdAt: string;
  updatedAt: string;
}

export const PERK_SLOTS = 3;

export function createProfile(id: string, displayName?: string, now = new Date()): Profile {
  const iso = now.toISOString();
  return {
    id,
    displayName,
    xp: 0,
    stats: emptyStats(),
    equipped: { ...DEFAULT_EQUIPPED },
    perks: [],
    createdAt: iso,
    updatedAt: iso,
  };
}

export function totalStars(stats: PlayerStats): number {
  return Object.values(stats.campaignStars).reduce((a, b) => a + b, 0);
}

export function profileContext(profile: Profile): UnlockContext {
  return { level: levelFromXp(profile.xp).level, stats: profile.stats, stars: totalStars(profile.stats) };
}

export interface MatchRewards {
  xpGained: number;
  breakdown: XpBreakdown;
  levelBefore: LevelInfo;
  levelAfter: LevelInfo;
  newUnlocks: UnlockDef[];
  /** Campaign stars newly earned this match (0 outside campaign). */
  starsEarned: number;
}

export interface ApplyMatchInput {
  events: readonly MatchEvent[];
  outcome: PlayerOutcome;
  mode: GameMode;
  /** YYYY-MM-DD in the server's chosen timezone; enables the daily first-win bonus. */
  today?: string;
  now?: Date;
}

/** Pure: returns a new profile plus what the results screen should show. */
export function applyMatch(profile: Profile, input: ApplyMatchInput): { profile: Profile; rewards: MatchRewards } {
  const { events, outcome, mode } = input;
  if (outcome.playerId !== profile.id) {
    throw new Error(`outcome is for ${outcome.playerId}, profile is ${profile.id}`);
  }
  const before = profileContext(profile);
  const stats: PlayerStats = { ...profile.stats, campaignStars: { ...profile.stats.campaignStars } };
  const parts: XpBreakdown[] = [scoreMatch(profile.id, events, outcome, mode)];

  for (const e of events) {
    if (e.actor !== profile.id) continue;
    if (e.type === 'hit' && e.relation === 'enemy' && e.damage > 0) {
      stats.hits++;
      if (e.headshot) stats.headshots++;
      if (e.direct) stats.directHits++;
      if (e.distanceFraction >= 0.6) stats.longShots++;
    } else if (e.type === 'kill' && e.relation === 'enemy') {
      stats.kills++;
      if (e.cause === 'drown') stats.drownKills++;
    }
  }
  stats.matches++;
  if (outcome.result === 'win') {
    stats.wins++;
    if (isFlawless(outcome)) stats.flawlessWins++;
    if (input.today && stats.lastWinDay !== input.today) {
      stats.lastWinDay = input.today;
      parts.push(dailyFirstWinLine());
    }
  }

  let starsEarned = 0;
  if (mode === 'campaign' && outcome.campaign) {
    const { levelId, stars } = outcome.campaign;
    const previous = stats.campaignStars[levelId] ?? 0;
    if (stars > previous) {
      starsEarned = stars - previous;
      stats.campaignStars[levelId] = stars;
      parts.push(starsLine(starsEarned));
    }
  }

  const breakdown = mergeBreakdowns(...parts);
  const next: Profile = {
    ...profile,
    xp: profile.xp + breakdown.total,
    stats,
    updatedAt: (input.now ?? new Date()).toISOString(),
  };
  const after = profileContext(next);
  const had = new Set(unlockedIds(before));
  const newUnlocks = unlockedIds(after)
    .filter((id) => !had.has(id))
    .map((id) => getUnlock(id)!);

  return {
    profile: next,
    rewards: {
      xpGained: breakdown.total,
      breakdown,
      levelBefore: levelFromXp(profile.xp),
      levelAfter: levelFromXp(next.xp),
      newUnlocks,
      starsEarned,
    },
  };
}

export type CosmeticKind = Exclude<UnlockKind, 'perk'>;

/** Equip a cosmetic. Throws if it is unknown, the wrong kind, or still locked. */
export function equip(profile: Profile, kind: CosmeticKind, id: string): Profile {
  const def = getUnlock(id);
  if (!def) throw new Error(`unknown unlock ${id}`);
  if (def.kind !== kind) throw new Error(`${id} is a ${def.kind}, not a ${kind}`);
  if (!isUnlocked(def, profileContext(profile))) throw new Error(`${id} is locked`);
  return { ...profile, equipped: { ...profile.equipped, [kind]: id }, updatedAt: new Date().toISOString() };
}

/** Replace the perk loadout. Throws on unknown, locked, duplicate or too many perks. */
export function setPerks(profile: Profile, ids: readonly string[]): Profile {
  if (ids.length > PERK_SLOTS) throw new Error(`at most ${PERK_SLOTS} perks`);
  if (new Set(ids).size !== ids.length) throw new Error('duplicate perk');
  const ctx = profileContext(profile);
  for (const id of ids) {
    const def = getUnlock(id);
    if (!def || def.kind !== 'perk') throw new Error(`${id} is not a perk`);
    if (!isUnlocked(def, ctx)) throw new Error(`${id} is locked`);
  }
  return { ...profile, perks: [...ids], updatedAt: new Date().toISOString() };
}

/** The perks that actually apply in a mode: always empty in Versus. */
export function activePerks(profile: Profile, mode: GameMode): UnlockDef[] {
  if (!isPveMode(mode)) return [];
  const ctx = profileContext(profile);
  return profile.perks
    .map((id) => getUnlock(id))
    .filter((d): d is UnlockDef => !!d && d.kind === 'perk' && isUnlocked(d, ctx));
}

/** Drop anything the profile no longer qualifies for (e.g. after a rules change). */
export function sanitizeProfile(profile: Profile): Profile {
  const ctx = profileContext(profile);
  const ok = (id: string, kind: UnlockKind) => {
    const d = getUnlock(id);
    return !!d && d.kind === kind && isUnlocked(d, ctx);
  };
  return {
    ...profile,
    equipped: {
      body: ok(profile.equipped.body, 'body') ? profile.equipped.body : DEFAULT_EQUIPPED.body,
      outfit: ok(profile.equipped.outfit, 'outfit') ? profile.equipped.outfit : DEFAULT_EQUIPPED.outfit,
      trail: ok(profile.equipped.trail, 'trail') ? profile.equipped.trail : DEFAULT_EQUIPPED.trail,
      ...(profile.equipped.custom ? { custom: profile.equipped.custom } : {}),
    },
    perks: profile.perks.filter((id) => ok(id, 'perk')).slice(0, PERK_SLOTS),
  };
}
